<template>
    <div style="width: 100%; min-height: 500px; height: 100%; position: relative;" :key="key">
        <button v-if="pipelineConfig && showresetViewBtn" class="btn btn-sm btn-light position-absolute"
            @click="$emit('resetView')" title="Reset View" style="z-index: 1000; top: 0px; right: 0px;">
            <i class="fa-solid fa-rotate"></i>
        </button>
        <button v-if="pipelineConfig && showresetViewBtn" class="btn btn-sm btn-light position-absolute"
            @click="zoomOut" title="Zoom Out" style="z-index: 1000; top: 0px; right: 36px;">
            <i class="fa-solid fa-magnifying-glass-minus"></i>
        </button>
        <button v-if="pipelineConfig && showresetViewBtn" class="btn btn-sm btn-light position-absolute" @click="zoomIn"
            title="Zoom In" style="z-index: 1000; top: 0px; right: 72px;">
            <i class="fa-solid fa-magnifying-glass-plus"></i>
        </button>
        <vue-flow ref="vueFlowRef" v-model="elements" :default-zoom="1" :min-zoom="0.05" :max-zoom="4"
            :fit-view-on-init="true" :node-dimensions="nodeDimensions" @nodeMouseEnter="showTooltip"
            @nodeMouseLeave="hideTooltip" @nodeClick="onNodeClick">
            <template #node-default="{ label, data }">
                <Handle type="target" :position="Position.Left" />
                <div v-if="data?.status?.toLowerCase() === 'failed' && !data?.markedSuccess"
                    class="node-floating-icon failed">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div class="vue-flow__node-label">
                    <div class="d-flex align-items-center justify-content-center gap-2">
                        <i v-if="data?.skip" class="fa-solid fa-forward fs-3 text-primary"></i>
                        <i v-if="data?.markedSuccess" class="fa-solid fa-check-circle fs-3 marked-success-icon"></i>
                        <div class="font-monospace fw-bold">
                            {{ label }}
                        </div>
                    </div>
                </div>
                <Handle type="source" :position="Position.Right" />
            </template>
            <background pattern-color="#aaa" gap="16" />
            <controls />
            <mini-map />
        </vue-flow>
    </div>
</template>

<script>
import { VueFlow, Background, Controls, MiniMap, Handle, Position } from '@vue-flow/core';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import YAML from 'yaml';

// node alignment configuration
const NODE_WIDTH = 250;
const NODE_HEIGHT = 50;
const X_GAP = 80; // Gap between sequential nodes (now horizontal)
const Y_GAP = 60; // Gap between parallel nodes (now vertical)
const CHAR_WIDTH = 12; // Approximate pixels per character
const MIN_WIDTH = 100;
const PADDING = 0; // Left + Right padding inside the box

export default {
    name: 'PipelineFlow',
    components: {
        VueFlow,
        Background,
        Controls,
        MiniMap,
        Handle,
    },
    props: {
        pipelineExecutionDetails: {
            type: Object,
            default: () => null
        },
        pipelineJobConfig: {
            type: Object,
            default: () => null
        },
        jobs: {
            type: Array,
            default: () => []
        },
        countsMetrics: {
            type: Object,
            default: () => { }
        },
        key: {
            type: String,
            default: () => 'pipeline-graph'
        },
        showresetViewBtn: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            elements: [],
            nodeDimensions: { width: NODE_WIDTH, height: NODE_HEIGHT },
            statusColorClass: {
                'running': 'running-node',
                'succeeded': 'succeeded-node',
                'failed': 'failed-node',
                'cancelled': 'cancelled-node',
                'submitted': 'submitted-node',
                'not_started': 'waiting-node',
                'waiting': 'waiting-node' // fallback
            },

            idCounter: 0, // for unique node id generation
            Position: Position,
        };
    },
    computed: {
        /**
         * Primary config source: reconstruct the pipeline config tree from
         * pipelineExecutionDetails.tasks[].full_path when available.
         * Falls back to YAML-parsed pipelineJobConfig when tasks are absent.
         */
        pipelineConfig() {
            // --- NEW: build from task full_paths if we have them ---
            const tasks = this.pipelineExecutionDetails?.tasks;
            if (tasks && tasks.length > 0) {
                return this.buildConfigFromTasks(tasks);
            }

            // --- FALLBACK: original YAML-based logic ---
            if (!this.pipelineJobConfig?.system_files) return null;
            const configFile = this.pipelineJobConfig.system_files.find(
                f => f.relative_path === 'config.yaml'
            );
            const varsFile = this.pipelineJobConfig.system_files.find(
                f => f.relative_path === 'vars.yaml'
            );
            if (!configFile?.content) return null;
            try {
                const mergedFile = (varsFile?.content || '') + '\n' + (configFile?.content || '');
                const documents = YAML.parseAllDocuments(mergedFile);
                for (const doc of documents) {
                    const parsed = doc.toJS();
                    if (!parsed) continue;
                    if (parsed.in_sequence) return { in_sequence: parsed.in_sequence };
                    if (parsed.in_parallel) return { in_parallel: parsed.in_parallel };
                    if (parsed.in_loop) return { in_loop: parsed.in_loop };
                }
                return null;
            } catch (e) {
                console.error('YAML parse error', e);
                return null;
            }
        }
    },
    mounted() {
        if (this.pipelineConfig) {
            this.generateFlow();
        }
    },
    methods: {
        zoomIn() {
            this.$refs.vueFlowRef?.zoomIn({ duration: 200 });
        },
        zoomOut() {
            this.$refs.vueFlowRef?.zoomOut({ duration: 200 });
        },
        // ─────────────────────────────────────────────────────────────────────
        // NEW: Reconstruct a pipelineConfig-compatible tree from task full_paths
        // ─────────────────────────────────────────────────────────────────────

        /**
         * Parse a full_path string into an array of typed segments.
         *
         * in_parallel / in_sequence   → { type, index }  (array containers)
         * in_loop                     → { type:'in_loop', iterIndex }
         *                               treated as a SINGLE wrapper node;
         *                               the numeric child is the iteration index.
         * anything else at the leaf   → { type:'job', name }
         *
         * e.g. "/in_parallel/1/in_sequence/2/in_loop/0/in_sequence/0/some-job"
         *   → [ {type:'in_parallel',index:1},
         *       {type:'in_sequence',index:2},
         *       {type:'in_loop',iterIndex:0},
         *       {type:'in_sequence',index:0},
         *       {type:'job',name:'some-job'} ]
         */
        parsePath(fullPath) {
            const ARRAY_CONTAINERS = new Set(['in_parallel', 'in_sequence']);
            const parts = fullPath.split('/').filter(Boolean);
            const segments = [];
            let i = 0;
            while (i < parts.length) {
                const part = parts[i];
                if (ARRAY_CONTAINERS.has(part)) {
                    const nextPart = parts[i + 1];
                    const index = nextPart !== undefined && /^\d+$/.test(nextPart)
                        ? parseInt(nextPart, 10)
                        : 0;
                    segments.push({ type: part, index });
                    i += /^\d+$/.test(nextPart ?? '') ? 2 : 1;
                } else if (part === 'in_loop') {
                    // in_loop is a single object, not an array.
                    // The next numeric token is the *iteration index* – we record it
                    // so we can count total iterations, but we do NOT use it as an
                    // array index when building the tree.
                    const nextPart = parts[i + 1];
                    const iterIndex = nextPart !== undefined && /^\d+$/.test(nextPart)
                        ? parseInt(nextPart, 10)
                        : 0;
                    segments.push({ type: 'in_loop', iterIndex });
                    i += /^\d+$/.test(nextPart ?? '') ? 2 : 1;
                } else if (/^\d+$/.test(part)) {
                    // standalone numeric — already consumed above, skip
                    i++;
                } else {
                    // leaf = job name
                    segments.push({ type: 'job', name: part });
                    i++;
                }
            }
            return segments;
        },

        /**
         * Walk the segments array and insert a job_name node into the mutable
         * config tree, creating structural containers on the fly.
         *
         * Rules:
         *   in_parallel / in_sequence  → node[key] is an Array; index into it.
         *   in_loop                    → node.in_loop is a single Object (not Array);
         *                                descend into it directly.
         *                                Track the max iterIndex seen so we can
         *                                store _iterCount on the wrapper later.
         *   job leaf                   → set node.job_name (only if not already set,
         *                                so the first iteration wins).
         */
        insertIntoTree(root, segments, iterCounts) {
            let node = root;

            for (let i = 0; i < segments.length; i++) {
                const seg = segments[i];

                if (seg.type === 'job') {
                    // leaf – nothing further to traverse
                    return;
                }

                if (seg.type === 'in_loop') {
                    // in_loop is a SINGLE container object on the parent node.
                    if (!node.in_loop) node.in_loop = {};

                    // Track the iteration count (max iterIndex + 1) on a side-channel
                    // map keyed by a stable path string up to this point.
                    const pathKey = this._loopPathKey(segments, i);
                    if (!iterCounts[pathKey]) iterCounts[pathKey] = 0;
                    iterCounts[pathKey] = Math.max(iterCounts[pathKey], seg.iterIndex + 1);

                    // Only descend further if this is the FIRST iteration (iterIndex 0),
                    // so iterations 1, 2, … don't overwrite the structure we built from 0.
                    if (seg.iterIndex === 0) {
                        node = node.in_loop;
                    } else {
                        // Skip this entire path — structure already established by iter 0
                        return;
                    }
                    continue;
                }

                // in_parallel or in_sequence — array container
                const key = seg.type;
                const idx = seg.index;

                if (!node[key]) node[key] = [];

                // Grow the array to accommodate this index, using null placeholders
                // so we never create a spurious {} that getDimensions would choke on.
                while (node[key].length <= idx) node[key].push(null);

                const isLastStructural = (i === segments.length - 2 && segments[i + 1]?.type === 'job');

                if (isLastStructural) {
                    // Next segment is the job leaf – place job_name here only once.
                    const jobName = segments[i + 1].name;
                    if (!node[key][idx] || !node[key][idx].job_name) {
                        // Reconstruct the canonical full_path for this leaf
                        const fullPath = '/' + segments.map((s, si) => {
                            if (s.type === 'in_loop') return `in_loop/${s.iterIndex}`;
                            if (s.type === 'job') return s.name;
                            return `${s.type}/${s.index}`;
                        }).join('/');
                        node[key][idx] = { job_name: jobName, _full_path: fullPath };
                    }
                    return;
                }

                // Ensure the slot is a real object before descending
                if (!node[key][idx] || node[key][idx] === null) {
                    node[key][idx] = {};
                }

                node = node[key][idx];
            }
        },

        /** Build a stable string key for identifying a particular in_loop site */
        _loopPathKey(segments, loopSegIndex) {
            return segments.slice(0, loopSegIndex + 1).map(s =>
                s.type === 'in_loop' ? 'in_loop'
                    : s.type === 'job' ? s.name
                        : `${s.type}[${s.index}]`
            ).join('/');
        },

        /**
         * Build a pipelineConfig-compatible tree from an array of task objects,
         * each having a `full_path` property.
         */
        buildConfigFromTasks(tasks) {
            if (!tasks || tasks.length === 0) return null;

            const root = {};
            const iterCounts = {}; // pathKey → max iteration count seen

            for (const task of tasks) {
                const segments = this.parsePath(task.full_path);
                this.insertIntoTree(root, segments, iterCounts);
            }

            // Now attach _iterCount to every in_loop node in the tree.
            // We stored keys like "in_parallel[1]/in_sequence[2]/in_loop".
            // Walk the iterCounts map and annotate the tree.
            this._applyIterCounts(root, [], iterCounts);

            // Remove any null slots left from array pre-allocation
            this._cleanNulls(root);

            // The root should have exactly one top-level key
            if (root.in_parallel) return { in_parallel: root.in_parallel };
            if (root.in_sequence) return { in_sequence: root.in_sequence };
            if (root.in_loop) return { in_loop: root.in_loop };

            return root;
        },

        /**
         * Recursively walk the tree, keeping track of the current path prefix,
         * and set _iterCount on every in_loop node we find.
         */
        _applyIterCounts(node, pathParts, iterCounts) {
            if (!node || typeof node !== 'object') return;

            if (node.in_loop !== undefined) {
                // Build the key that insertIntoTree would have used
                const key = [...pathParts, 'in_loop'].join('/');
                if (iterCounts[key] !== undefined) {
                    node._iterCount = iterCounts[key];
                }
                this._applyIterCounts(node.in_loop, [...pathParts, 'in_loop'], iterCounts);
            }

            for (const containerKey of ['in_parallel', 'in_sequence']) {
                if (Array.isArray(node[containerKey])) {
                    node[containerKey].forEach((child, idx) => {
                        this._applyIterCounts(child, [...pathParts, `${containerKey}[${idx}]`], iterCounts);
                    });
                }
            }
        },

        /** Remove null entries from all in_parallel / in_sequence arrays */
        _cleanNulls(node) {
            if (!node || typeof node !== 'object') return;
            for (const key of ['in_parallel', 'in_sequence']) {
                if (Array.isArray(node[key])) {
                    node[key] = node[key].filter(c => c !== null);
                    node[key].forEach(child => this._cleanNulls(child));
                }
            }
            if (node.in_loop) this._cleanNulls(node.in_loop);
        },

        // ─────────────────────────────────────────────────────────────────────
        // Existing methods (unchanged)
        // ─────────────────────────────────────────────────────────────────────

        nextId(name = 'job') {
            return `${name}-${++this.idCounter}`;
        },
        getJobKind(jobName) {
            const job = this.jobs?.[jobName];
            return job?.kind || "N/A";
        },
        showTooltip(event) {
            const { id } = event.node;
            const mouseEvent = event.event;
            const node = this.elements.find(el => el.id === id && !el.source);

            if (!node || node.data?.kind === 'loop-wrapper' || node.data?.kind === 'loop') return;

            const statusIconMap = {
                'running': { icon: 'fa-spinner fa-spin', color: '#b38600' },
                'succeeded': { icon: 'fa-circle-check', color: 'var(--bs-success)' },
                'failed': { icon: 'fa-circle-xmark', color: 'var(--bs-danger)' },
                'cancelled': { icon: 'fa-ban', color: '#915b1d' },
                'submitted': { icon: 'fa-clock', color: '#6c757d' },
                'waiting': { icon: 'fa-hourglass-end', color: '#6c757d' },
                'not_started': { icon: 'fa-hourglass-half', color: '#6c757d' },
            };

            const statusKey = (node.data.status || '').toLowerCase();
            const statusMeta = statusIconMap[statusKey] || { icon: 'fa-circle-question', color: '#6c757d' };

            const rows = [
                { icon: 'fa-tag', color: '#3b82f6', label: 'NAME', value: node.data.name || 'N/A' },
                { icon: 'fa-rectangle-list', color: '#3b82f6', label: 'KIND', value: node.data.kind || 'N/A' },
                { icon: statusMeta.icon, color: statusMeta.color, label: 'STATUS', value: (node.data.status || 'N/A').toUpperCase() },
            ];

            if (node.data.skip) {
                rows.push({ icon: 'fa-forward', color: '#3b82f6', label: 'SKIP', value: 'Yes' });
            }
            if (node.data.markedSuccess) {
                rows.push({ icon: 'fa-file-circle-check', color: '#03b600', label: 'MARKED SUCCESS', value: 'Yes' });
            }
            if (node.data.record_count) {
                rows.push({ icon: 'fa-database', color: '#3b82f6', label: 'RECORD COUNT', value: node.data.record_count });
            }

            const tooltip = document.createElement('div');
            tooltip.classList.add('vueflow-tooltip');

            const header = document.createElement('div');
            header.classList.add('vft-header');
            header.textContent = 'JOB DETAILS';
            tooltip.appendChild(header);

            const body = document.createElement('div');
            body.classList.add('vft-body');

            rows.forEach(r => {
                const row = document.createElement('div');
                row.classList.add('vft-row');

                const icon = document.createElement('i');
                icon.classList.add('fa-solid', ...r.icon.split(' '), 'vft-icon');
                icon.style.color = r.color;

                const rowContent = document.createElement('div');
                rowContent.classList.add('vft-row-content');

                const label = document.createElement('div');
                label.classList.add('vft-label');
                label.textContent = r.label;

                const value = document.createElement('div');
                value.classList.add('vft-value');
                value.textContent = r.value;

                rowContent.appendChild(label);
                rowContent.appendChild(value);
                row.appendChild(icon);
                row.appendChild(rowContent);
                body.appendChild(row);
            });

            tooltip.appendChild(body);

            tooltip.style.left = mouseEvent.pageX + 12 + 'px';
            tooltip.style.top = mouseEvent.pageY + 12 + 'px';

            this.hideTooltip();
            document.body.appendChild(tooltip);
        },
        hideTooltip() {
            const existingTooltip = document.querySelector('.vueflow-tooltip');
            if (existingTooltip) {
                existingTooltip.remove();
            }
        },
        onNodeClick(event) {
            this.$emit('nodeClick', {
                name: event.node.data.name,
                full_path: event.node.data.full_path || null,
            });
        },

        calculateNodeWidth(label) {
            const textWidth = label.length * CHAR_WIDTH;
            return Math.max(MIN_WIDTH, textWidth + PADDING);
        },

        generateFlow() {
            this.idCounter = 0;
            const layoutTree = this.getDimensions(this.pipelineConfig);
            const { nodes, edges } = this.buildFlow(layoutTree, 0, 0, layoutTree.width, layoutTree.height);
            this.elements = [...nodes, ...edges];
        },

        getDimensions(node) {
            // Guard: skip empty / unrecognised nodes
            if (!node || typeof node !== 'object') return { width: 0, height: 0 };
            if (!node.job_name && !node.in_sequence && !node.in_parallel && !node.in_loop) {
                return { width: 0, height: 0 };
            }

            if (node.job_name) {
                const dynamicWidth = this.calculateNodeWidth(node.job_name);
                const { status, markedSuccess, skip } = this.resolveJobExecution(node.job_name, node._full_path);
                return {
                    ...node,
                    width: dynamicWidth + (skip ? 28 : 0) + (markedSuccess ? 28 : 0),
                    height: 50
                };
            }

            if (node.in_sequence) {
                const children = node.in_sequence
                    .map(child => this.getDimensions(child))
                    .filter(c => c.width > 0 || c.height > 0);
                if (children.length === 0) return { width: 0, height: 0 };
                return {
                    in_sequence: children,
                    width: children.reduce((sum, c) => sum + c.width, 0) + (children.length - 1) * X_GAP,
                    height: Math.max(...children.map(c => c.height))
                };
            }

            if (node.in_parallel) {
                const children = node.in_parallel
                    .map(child => this.getDimensions(child))
                    .filter(c => c.width > 0 || c.height > 0);
                if (children.length === 0) return { width: 0, height: 0 };
                return {
                    in_parallel: children,
                    width: Math.max(...children.map(c => c.width)),
                    height: children.reduce((sum, c) => sum + c.height, 0) + (children.length - 1) * Y_GAP
                };
            }

            if (node.in_loop) {
                const LOOP_PADDING = 20;
                const LOOP_BADGE_HEIGHT = 36;

                const innerNode = node.in_loop.job_name
                    ? { job_name: node.in_loop.job_name }
                    : node.in_loop.in_parallel
                        ? { in_parallel: node.in_loop.in_parallel }
                        : node.in_loop.in_sequence
                            ? { in_sequence: node.in_loop.in_sequence }
                            : null;

                if (!innerNode) return { width: 0, height: 0 };

                const inner = this.getDimensions(innerNode);
                if (inner.width === 0 && inner.height === 0) return { width: 0, height: 0 };

                // iterCount: prefer _iterCount (set by buildConfigFromTasks),
                // fall back to loop_data (YAML path)
                let iterCount = node._iterCount || 0;
                if (!iterCount && node.loop_data) {
                    iterCount = Object.values(node.loop_data).reduce((max, arr) =>
                        Array.isArray(arr) ? Math.max(max, arr.length) : max, 0);
                }

                return {
                    in_loop: { ...node.in_loop, _inner: inner },
                    loop_name: node.loop_name,
                    loop_data: node.loop_data,
                    max_parallel: node.max_parallel,
                    _inner: inner,
                    _iterCount: iterCount,
                    width: inner.width + LOOP_PADDING * 2,
                    height: inner.height + LOOP_PADDING * 2 + LOOP_BADGE_HEIGHT,
                };
            }

            return { width: 0, height: 0 };
        },

        resolveJobExecution(jobName, fullPath) {
            // Look up by full_path first (exact task match), fall back to name bucket
            // for the YAML-config path where full_paths aren't stored.
            const allTasks = this.pipelineExecutionDetails?.tasks || null;
            let task = null;
            if (allTasks && fullPath) {
                task = allTasks.find(t => t.full_path === fullPath) || null;
            }

            // For in_loop nodes there are multiple tasks per name; we want the one
            // matching this exact path (already found above). For non-loop repeated
            // names (same job in different sequence positions) we also need the exact task.
            // Fallback: name-keyed bucket (YAML path or when fullPath not provided).
            const INACTIVE_STATUSES = new Set(['waiting', 'not_started', '']);

            let status;
            let markedSuccess = false;
            let recordCount = null;
            let skip = false;

            if (task) {
                // Single-task lookup by full_path
                status = task.status || "WAITING";
                markedSuccess = task.marked_success || false;
                skip = task.is_skipped || false;
                recordCount = this.countsMetrics?.[jobName]?.join(', ') || null;
            } else {
                // Name-bucket fallback (YAML path / loop display node)
                const executions = this.pipelineExecutionDetails?.executionDetails?.[jobName] || [];
                if (executions.length > 0) {
                    for (let i = executions.length - 1; i >= 0; i--) {
                        const exec = executions[i];
                        const s = (exec?.status || '').toLowerCase();
                        if (!INACTIVE_STATUSES.has(s)) {
                            status = exec?.status || "WAITING";
                            break;
                        }
                    }
                    markedSuccess = executions[executions.length - 1]?.marked_success || false;
                    recordCount = this.countsMetrics?.[jobName]?.join(', ') || null;
                    skip = executions[executions.length - 1]?.is_skipped || false;
                }
                if (!status) {
                    status = this.pipelineExecutionDetails?.executionDetails?.[jobName]?.[0]?.status || "WAITING";
                }
            }

            let statusClass = this.statusColorClass[(status || '').toLowerCase()] || 'waiting-node';
            if (markedSuccess) statusClass = 'succeeded-node-marked';

            return { status, markedSuccess, skip, recordCount };
        },

        buildFlow(node, startX, startY, containerW, containerH) {
            // Guard: skip empty / unrecognised nodes
            if (!node || typeof node !== 'object') return { nodes: [], edges: [], entryIds: [], exitIds: [] };
            if (node.width === 0 && node.height === 0) return { nodes: [], edges: [], entryIds: [], exitIds: [] };

            const xOffset = (containerW - node.width) / 2;
            const yOffset = (containerH - node.height) / 2;

            if (node.job_name) {
                const id = this.nextId(node.job_name);
                const fullPath = node._full_path || null;
                const { status, markedSuccess, skip, recordCount } = this.resolveJobExecution(node.job_name, fullPath);

                let statusClass = this.statusColorClass[(status || '').toLowerCase()] || 'waiting-node';
                if (markedSuccess) statusClass = 'succeeded-node-marked';

                return {
                    nodes: [{
                        id,
                        type: 'default',
                        position: { x: startX + xOffset, y: startY + yOffset },
                        label: node.job_name,
                        sourcePosition: 'right',
                        targetPosition: 'left',
                        style: {
                            width: `${node.width}px`,
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        },
                        class: statusClass + (skip ? ' skipped' : ''),
                        data: {
                            name: node.job_name,
                            full_path: fullPath,
                            kind: this.getJobKind(node.job_name),
                            status: status,
                            record_count: recordCount,
                            skip: skip,
                            markedSuccess: markedSuccess
                        }
                    }],
                    edges: [],
                    entryIds: [id],
                    exitIds: [id]
                };
            }

            if (node.in_sequence) {
                let currentX = startX;
                let nodes = [];
                let edges = [];
                let entryIds = [];
                let prevExitIds = [];

                node.in_sequence.forEach((child, index) => {
                    const result = this.buildFlow(child, currentX, startY, child.width, containerH);
                    nodes.push(...result.nodes);
                    edges.push(...result.edges);
                    if (index === 0) entryIds = result.entryIds;

                    prevExitIds.forEach(src =>
                        result.entryIds.forEach(tgt =>
                            edges.push({
                                id: `e-${src}-${tgt}`,
                                source: src,
                                target: tgt,
                                type: 'smoothstep',
                                animated: true,
                                // This ensures the line connects Right End to Left End
                                style: { stroke: this.$store.state.darkMode ? 'white' : 'black', strokeWidth: 2 }
                            })
                        )
                    );
                    prevExitIds = result.exitIds;
                    currentX += child.width + X_GAP;
                });
                return { nodes, edges, entryIds, exitIds: prevExitIds };
            }

            if (node.in_parallel) {
                let currentY = startY;
                let nodes = [];
                let edges = [];
                let entryIds = [];
                let exitIds = [];

                node.in_parallel.forEach(child => {
                    const result = this.buildFlow(child, startX, currentY, containerW, child.height);

                    nodes.push(...result.nodes);
                    edges.push(...result.edges);
                    entryIds.push(...result.entryIds);
                    exitIds.push(...result.exitIds);
                    currentY += child.height + Y_GAP;
                });
                return { nodes, edges, entryIds, exitIds };
            }

            if (node.in_loop) {
                const LOOP_PADDING = 20;
                const LOOP_BADGE_HEIGHT = 36;

                const innerNode = node.in_loop._inner || node._inner;
                const iterCount = node._iterCount || 0;
                const loopName = node.loop_name || 'loop';
                const loopLabel = `↻  ${loopName}  ·  runs ${iterCount}×`;

                const innerStartX = startX + LOOP_PADDING;
                const innerStartY = startY + LOOP_PADDING + LOOP_BADGE_HEIGHT;

                const result = this.buildFlow(
                    innerNode,
                    innerStartX,
                    innerStartY,
                    innerNode.width,
                    innerNode.height
                );

                const wrapperId = this.nextId('loop-wrapper');
                const wrapperNode = {
                    id: wrapperId,
                    type: 'default',
                    position: { x: startX, y: startY },
                    label: '',
                    draggable: false,
                    selectable: false,
                    style: {
                        width: `${node.width}px`,
                        height: `${node.height}px`,
                        border: '2px solid #BA7517',
                        borderRadius: '12px',
                        zIndex: -1,
                        pointerEvents: 'none',
                    },
                    class: 'loop-wrapper-node',
                    data: { name: '', kind: 'loop-wrapper', status: '', skip: false, markedSuccess: false },
                    zIndex: -1,
                };

                const badgeId = this.nextId('loop-badge');
                const badgeWidth = Math.max(140, loopLabel.length * 8 + 24);
                const badgeNode = {
                    id: badgeId,
                    type: 'default',
                    position: {
                        x: startX + (node.width - badgeWidth) / 2,
                        y: startY + 16,
                    },
                    label: loopLabel,
                    draggable: false,
                    selectable: false,
                    style: {
                        width: `${badgeWidth}px`,
                        height: '22px',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    },
                    class: 'loop-badge-node',
                    data: { name: loopLabel, kind: 'loop', status: '', skip: false, markedSuccess: false },
                };

                return {
                    nodes: [wrapperNode, badgeNode, ...result.nodes],
                    edges: [...result.edges],
                    entryIds: result.entryIds,
                    exitIds: result.exitIds,
                };
            }
        },
    },
    watch: {
        pipelineExecutionDetails: {
            handler(newVal) {
                if (newVal && this.pipelineConfig) {
                    this.idCounter = 0;
                    this.generateFlow();
                }
            }
        },
        '$store.state.darkMode': {
            handler(newVal) {
                this.generateFlow();
            }
        },
        jobs: {
            handler(newVal, oldValue) {
                if (newVal && newVal !== oldValue) {
                    this.idCounter = 0;
                    this.generateFlow();
                }
            }
        }
    },
    beforeUnmount() {
        // clear any tooltips before unmounting
        this.hideTooltip();
    },
    emits: ['nodeClick'],
};
</script>

<style lang="scss">
.vue-flow {
    width: 100%;
    height: 100%;
}

.vue-flow__node {
    cursor: pointer;
}

.vue-flow__edge path {
    stroke: #ccc;
    stroke-width: 2px;
}

.vue-flow__handle {
    opacity: 0 !important;
    background: transparent !important;
    border: none !important;
}

.vue-flow__node-default {
    border-radius: 5px;
    border: 1px solid #000000;
    color: #000000;
    font-size: 14px;
    font-weight: 500;
}

.vue-flow__node-default.running-node {
    background-color: #ffd118 !important;
    border: 2px solid black !important;
    overflow: hidden;
    animation: concourseGaps 2s infinite ease-out;
}

.vue-flow__node-default.running-node::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    background-image: repeating-linear-gradient(135deg,
            rgba(0, 0, 0, 0.05) 0px,
            rgba(0, 0, 0, 0.05) 6px,
            rgba(0, 0, 0, 0.25) 6px,
            rgba(0, 0, 0, 0.25) 16px);
    background-size: 68px 68px;
    animation: diagonalFlow 4s linear infinite;
    opacity: 0.35;
}

@keyframes concourseGaps {
    0% {
        box-shadow:
            0 0 0 2px rgba(255, 215, 0, 0.8),
            0 0 0 13px rgba(255, 215, 0, 0),
            0 0 0 15px rgba(255, 215, 0, 0.4),
            0 0 0 20px rgba(255, 215, 0, 0);
    }

    100% {
        box-shadow:
            0 0 0 15px rgba(255, 215, 0, 0.4),
            0 0 0 20px rgba(255, 215, 0, 0),
            0 0 0 22px rgba(255, 215, 0, 0),
            0 0 0 30px rgba(255, 215, 0, 0);
    }
}

@keyframes diagonalFlow {
    from {
        background-position: 0px 0px;
    }

    to {
        background-position: 68px 68px;
    }
}

.vue-flow__node-default.succeeded-node {
    background-color: var(--bs-success) !important;
    border: none !important;
}

.vue-flow__node-default.succeeded-node-marked {
    background-color: #97ffd4 !important;
    color: #006f41 !important;
    border: 2px solid #006f41 !important;
}

.marked-success-icon {
    color: #006f41 !important;
}

.vue-flow__node-default.failed-node,
.vue-flow__node-default.failed-node:hover {
    background-color: #e63757;
    color: white;
    border: none;
    box-shadow:
        0 0 0 6px rgb(255, 255, 255),
        0 0 0 14px rgba(230, 55, 87, 0.541);
}

.vue-flow__node-default.submitted-node {
    background-color: #acb4c1;
    border: none !important;
}

.vue-flow__node-default.cancelled-node {
    background-color: #915b1d !important;
    color: white !important;
    border: none !important;
}

.vue-flow__node-default.waiting-node {
    background-color: #EDF2F9;
}

.vue-flow__node-default.waiting-node.skipped {
    border: 2px solid var(--bs-primary) !important;
    background-color: #d2e5ff;
}

.node-floating-icon {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: -40px;
    font-size: 24px;
    z-index: 10;
    line-height: 1;
    color: #e63757;
}

.vue-flow__edge-path {
    stroke: #000000 !important;
    stroke-width: 2px;
}

.vue-flow__node-default.loop-wrapper-node {
    background-color: rgb(255, 237, 211);
    border: 2px solid #BA7517 !important;
    border-radius: 12px !important;
    pointer-events: none !important;
    cursor: default !important;
    box-shadow: none !important;
    padding: 0 !important;
}

.vue-flow__node-default.loop-wrapper-node .vue-flow__node-label {
    display: none !important;
}

.vue-flow__node-default.loop-badge-node {
    background-color: #ffd792;
    color: #854F0B !important;
    border: 1.5px dashed #BA7517 !important;
    border-radius: 6px !important;
    font-size: 11px !important;
    font-family: monospace;
    pointer-events: none !important;
    box-shadow: none !important;
}

.vueflow-tooltip {
    position: absolute;
    pointer-events: none;
    z-index: 1000;
    background: #ffffff !important;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.13), 0 1px 4px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    font-family: inherit;
}

.vft-header {
    color: #3b82f6;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    padding: 8px 8px 8px 8px;
    border-bottom: 1px solid #f0f4f8;
    background: #ffffff;
}

.vft-body {
    background: #ffffff;
}

.vft-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 3px 0px 3px;
}

.vft-icon {
    width: 18px;
    text-align: center;
    margin-top: 3px;
    flex-shrink: 0;
    font-size: 15px;
}

.vft-row-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.vft-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: #3b82f6;
    line-height: 1.2;
}

.vft-value {
    font-size: 13px;
    color: #1a202c;
    font-weight: 400;
    line-height: 1.4;
    word-break: break-all;
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .vue-flow__edge path {
        stroke: $dark-text-primary !important;
        stroke-width: 2px;
    }

    .vue-flow__node-default.failed-node,
    .vue-flow__node-default.failed-node:hover {
        background-color: #e63757 !important;
        color: rgb(0, 0, 0) !important;
        border: none !important;
        box-shadow:
            0 0 0 6px $dark-card-bg,
            0 0 0 14px rgba(230, 55, 87, 0.541) !important;
    }

    .vue-flow__node-default.waiting-node.skipped {
        border: 2px solid var(--bs-primary) !important;
        background-color: #002a65 !important;
        color: $dark-text-primary !important;
    }

    .vue-flow__node-default.succeeded-node-marked {
        color: #97ffd4 !important;
        background-color: #006f41 !important;
        border: 2px solid #97ffd4 !important;
    }

    .marked-success-icon {
        color: #97ffd4 !important;
    }

    .vue-flow__node-default.waiting-node {
        background-color: #5f6164 !important;
        color: $dark-text-primary !important;
    }

    .vue-flow__node-default.submitted-node {
        background-color: #acb4c1 !important;
    }

    .vue-flow__node-default.loop-wrapper-node {
        background-color: rgba(255, 149, 0, 0.223) !important;
        border: 2px solid #BA7517 !important;
        border-radius: 12px !important;
        pointer-events: none !important;
        cursor: default !important;
        box-shadow: none !important;
        padding: 0 !important;
    }

    .vue-flow__node-default.loop-badge-node {
        background-color: rgba(255, 149, 0, 0.202) !important;
        color: #f59e0b !important;
        border: 1.5px dashed #BA7517 !important;
        border-radius: 6px !important;
        font-size: 11px !important;
        font-family: monospace;
        pointer-events: none !important;
        box-shadow: none !important;
    }

    .vueflow-tooltip {
        background: $dark-elevated-bg !important;
    }

    .vft-header {
        border-bottom: 1px solid #4d4d4d !important;
        background: $dark-elevated-bg !important;
    }

    .vft-body {
        background: $dark-elevated-bg !important;
    }

    .vft-value {
        color: $dark-text-primary !important;
    }
}
</style>